import * as React from 'react';
import { uiMarkerInternal } from '../recipes/shared';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { IconTile } from './Icon';
import { Text } from './Text';

export interface EmptyStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Shown in a tinted tile above the title. */
  icon?: LucideIcon;
  title: React.ReactNode;
  /** One line on why it is empty, or what to do about it. */
  description?: React.ReactNode;
  /**
   * A quieter line under the description — accepted file types, size limits.
   * Three of the five empty states this replaced had one.
   */
  footnote?: React.ReactNode;
  /** The way out — usually the button that creates the first one. */
  action?: React.ReactNode;
  size?: 'sm' | 'md';
}

/**
 * "There is nothing here yet."
 *
 * The shape every list, table and panel needs and nine of them were building by
 * hand: a tinted icon tile, a title, a line of explanation, sometimes an
 * action. They had drifted on every axis — icon size, gap, whether the text was
 * centred, whether the tile was a circle.
 *
 * Use it for *empty*, not for *failed* — an error belongs in an Alert, where
 * the tone carries the meaning.
 */
export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  function EmptyState(
    {
      icon,
      title,
      description,
      footnote,
      action,
      size = 'md',
      className,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          uiMarkerInternal,
          'flex flex-col items-center justify-center px-6 text-center',
          size === 'sm' ? 'py-8' : 'py-12',
          className
        )}
        {...props}
      >
        {icon ? (
          <IconTile
            icon={icon}
            size={size === 'sm' ? 10 : 12}
            className="mb-4 rounded-pill"
          />
        ) : null}
        <Text variant={size === 'sm' ? 'body-sm-strong' : 'body-strong'}>
          {title}
        </Text>
        {description ? (
          <Text
            variant="body-sm"
            tone="muted"
            className="mt-1 max-w-[42ch] text-balance"
          >
            {description}
          </Text>
        ) : null}
        {footnote ? (
          <Text variant="caption" tone="muted" className="mt-2 max-w-[48ch]">
            {footnote}
          </Text>
        ) : null}
        {action ? <div className="relay-ui-internal mt-4">{action}</div> : null}
      </div>
    );
  }
);
